import type { TFunction } from "i18next";
import type { TourStep } from "../TourProvider";
import { queryTourTarget } from "../utils/queryTourTarget";

/**
 * Get the current active tab from the URL or DOM
 */
const getCurrentTab = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get("tab");
  if (tabFromUrl) {
    return tabFromUrl;
  }

  const activeTab = document.querySelector(
    ".ant-tabs-tab-active"
  ) as HTMLElement | null;
  if (activeTab) {
    return activeTab.getAttribute("data-node-key") || "details";
  }

  return "details";
};

const TAB_ORDER = ["details", "members", "join-requests"];

/**
 * First member details path in the members table (for handoff).
 */
const getFirstMemberPath = (): string | null => {
  const link = document.querySelector(
    "[data-tour='org-members-rows'] a[href*='/member/']"
  ) as HTMLAnchorElement | null;
  if (!link?.pathname) {
    return null;
  }
  return link.pathname;
};

const getDetailsTabSteps = (t: TFunction): TourStep[] => [
  {
    title: t("Tour_OrgDetails_Welcome_Title"),
    description: t("Tour_OrgDetails_Welcome_Description"),
    target: () => queryTourTarget("org-details-header"),
    placement: "bottom",
  },
  {
    title: t("Tour_OrgDetails_Info_Title"),
    description: t("Tour_OrgDetails_Info_Description"),
    target: () => queryTourTarget("org-info"),
    placement: "top",
  },
  {
    title: t("Tour_OrgDetails_Actions_Title"),
    description: t("Tour_OrgDetails_Actions_Description"),
    target: () => queryTourTarget("org-action-buttons"),
    placement: "bottom",
    tabNavigation: {
      currentTab: "details",
      tabOrder: TAB_ORDER,
    },
  },
];

const getMembersTabSteps = (t: TFunction): TourStep[] => {
  const firstMemberPath = getFirstMemberPath();

  return [
    {
      title: t("Tour_OrgDetails_Members_Title"),
      description: t("Tour_OrgDetails_Members_Description"),
      target: () => queryTourTarget("org-members-tab-content"),
      placement: "top",
    },
    {
      title: t("Tour_OrgDetails_Invite_Title"),
      description: t("Tour_OrgDetails_Invite_Description"),
      target: () => queryTourTarget("org-invite-button"),
      placement: "left",
    },
    {
      title: t("Tour_OrgDetails_MemberAccess_Title"),
      description: t("Tour_OrgDetails_MemberAccess_Description"),
      target: () => queryTourTarget("org-members-rows"),
      placement: "top",
      tabNavigation: {
        currentTab: "members",
        tabOrder: TAB_ORDER,
      },
      recommendedTours: firstMemberPath
        ? [
            {
              label: t("Tour_Recommended_MemberAccess"),
              path: firstMemberPath,
            },
          ]
        : [],
    },
  ];
};

const getJoinRequestsTabSteps = (t: TFunction): TourStep[] => [
  {
    title: t("Tour_OrgDetails_JoinRequests_Title"),
    description: t("Tour_OrgDetails_JoinRequests_Description"),
    target: () => queryTourTarget("org-join-requests-tab-content"),
    placement: "top",
    tabNavigation: {
      currentTab: "join-requests",
      tabOrder: TAB_ORDER,
    },
  },
];

/**
 * Tour configuration for the Organization Details Page (/organization/:organizationID)
 */
export const organizationDetailsTour = (t: TFunction): TourStep[] => {
  const currentTab = getCurrentTab();

  switch (currentTab) {
    case "details":
      return getDetailsTabSteps(t);
    case "members":
      return getMembersTabSteps(t);
    case "join-requests":
      return getJoinRequestsTabSteps(t);
    default:
      return getDetailsTabSteps(t);
  }
};
